import { Capacitor } from "@capacitor/core";

// Native-feeling feedback per the avo¢ash haptics matrix:
// light = selection (tab/toggle), medium = long-press context,
// success = payment sent / contribution confirmed / biometric OK,
// error = declined transaction / insufficient funds.
// Web fallback uses navigator.vibrate with matched patterns; every call is
// best-effort and never throws.

const HAPTICS_ENABLED_KEY = "avocash.haptics.enabled";

let nativeModule = null;
let nativeLoaded = false;

async function loadNative() {
  if (nativeLoaded) return nativeModule;
  nativeLoaded = true;
  if (!Capacitor.isNativePlatform()) return null;
  try {
    nativeModule = await import("@capacitor/haptics");
  } catch (e) {
    nativeModule = null;
  }
  return nativeModule;
}

function webEnabled() {
  try {
    return localStorage.getItem(HAPTICS_ENABLED_KEY) !== "false";
  } catch (e) {
    return true;
  }
}

export function hapticsEnabled() {
  return webEnabled();
}

export function setHapticsEnabled(enabled) {
  try {
    localStorage.setItem(HAPTICS_ENABLED_KEY, enabled ? "true" : "false");
  } catch (e) {
    /* storage unavailable — ignore */
  }
}

function vibrate(pattern) {
  try {
    if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
      navigator.vibrate(pattern);
    }
  } catch (e) {
    /* ignore */
  }
}

async function impact(style, fallbackPattern) {
  if (!webEnabled()) return;
  try {
    const mod = await loadNative();
    if (mod?.Haptics && mod?.ImpactStyle) {
      await mod.Haptics.impact({ style: mod.ImpactStyle[style] });
      return;
    }
  } catch (e) {
    /* fall through to web */
  }
  vibrate(fallbackPattern);
}

async function notify(type, fallbackPattern) {
  if (!webEnabled()) return;
  try {
    const mod = await loadNative();
    if (mod?.Haptics && mod?.NotificationType) {
      await mod.Haptics.notification({ type: mod.NotificationType[type] });
      return;
    }
  } catch (e) {
    /* fall through to web */
  }
  vibrate(fallbackPattern);
}

export function useHaptics() {
  return {
    /** Tab selection, toggles, chips, pull-to-refresh engaged */
    light: () => impact("Light", 10),
    /** Long-press context menus, opening an action sheet */
    medium: () => impact("Medium", 20),
    /** Rare, heavy confirmation moments */
    heavy: () => impact("Heavy", 30),
    /** Payment sent, contribution confirmed, biometric/PIN success */
    success: () => notify("Success", [10, 40, 10]),
    /** Declined transaction, insufficient funds, validation failure */
    error: () => notify("Error", [30, 50, 30]),
    /** Non-destructive informational completion */
    warning: () => notify("Warning", [15, 30, 15]),
  };
}

export default useHaptics;
